// Per-browser dark/light theme preference (no account/server setting). Defaults to dark
// until the user picks one. Applied by toggling the `dark` class on <html>, which
// drives Tailwind's class-based dark mode (see tailwind.config.js `darkMode: 'class'`).
//
// NOTE: the initial class is set by a tiny inline script in public/index.html BEFORE React mounts
// (to avoid a flash of the wrong theme). This module keeps it in sync and handles toggling, and
// MUST use the same storage key (`gc:theme`) as that script.

const THEME_KEY = "gc:theme";

export function getStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === "dark" || v === "light" ? v : null;
  } catch {
    return null;
  }
}

// Dark is the site default: a stored user choice always wins, but first-time visitors
// (nothing stored) get dark mode. Mirrored by the pre-paint script in public/index.html.
export function getInitialTheme() {
  return getStoredTheme() || "dark";
}

export function isDarkMode() {
  try {
    return document.documentElement.classList.contains("dark");
  } catch {
    return false;
  }
}

// Apply + persist a theme. Returns the normalized theme string.
export function applyTheme(theme) {
  const isDark = theme === "dark";
  try {
    document.documentElement.classList.toggle("dark", isDark);
  } catch {}
  try {
    localStorage.setItem(THEME_KEY, isDark ? "dark" : "light");
  } catch {}
  try {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", isDark ? "#0f172a" : "#ffffff");
  } catch {}
  // Let any other toggle in the UI (the floating button, the profile setting) reflect the change.
  try {
    window.dispatchEvent(new CustomEvent("gc:themechange", { detail: isDark ? "dark" : "light" }));
  } catch {}
  return isDark ? "dark" : "light";
}

// Flip to the opposite of whatever is currently applied. Returns the new theme string.
export function toggleTheme() {
  return applyTheme(isDarkMode() ? "light" : "dark");
}

// Mapbox style URL matching the current theme. Used when a map is created so it loads in the
// right theme. (An already-open map keeps its style until it is re-created.)
export function mapStyleUrl() {
  return isDarkMode() ? "mapbox://styles/mapbox/dark-v11" : "mapbox://styles/mapbox/streets-v12";
}
